import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import { useAppDispatch } from 'app/store/store';
import { DialogFormData } from 'common/receipt.types';
import { setSelectedItem } from 'features/itemSlice';
import { intl } from 'i18n/I18nProvider';
import { FC, useCallback } from 'react';
import { UseFieldArrayRemove } from 'react-hook-form';

interface RowsWithUUID extends DialogFormData {
  uuid: string;
}

interface FlattenedRow {
  id: string;
  name: string;
  intlUnitOfMeasure: string;
  quantity: number | string;
  unitPrice: number | string;
  totalPrice: number | string;
  uuid: string;
  isEcoTax: boolean;
  parentIndex: number;
}

interface ItemsTableProps {
  rows: RowsWithUUID[];
  onRemoveRow: UseFieldArrayRemove;
}

const styles = {
  tableContainer: {
    width: '100%',
    minHeight: 100,
    maxHeight: 300,
    overflowY: 'auto',
  },
};

export const ItemsTable: FC<ItemsTableProps> = ({ rows, onRemoveRow }) => {
  const dispatch = useAppDispatch();

  const flattenedRows: FlattenedRow[] = rows.flatMap((parent, parentIndex) => {
    const parentRow: FlattenedRow = {
      id: parent.id,
      name: parent.name,
      intlUnitOfMeasure: parent.intlUnitOfMeasure || parent.unitOfMeasure,
      quantity: parent.quantity,
      unitPrice: parent.unitPrice,
      totalPrice: parent.totalPrice,
      uuid: parent.uuid,
      isEcoTax: false,
      parentIndex,
    };

    const ecoTaxRows: FlattenedRow[] = parent.ecoTaxItems.map((ecoTaxItem, ecoTaxIndex) => ({
      id: ecoTaxItem.id,
      name: ecoTaxItem.name,
      intlUnitOfMeasure: parent.intlUnitOfMeasure || parent.unitOfMeasure,
      quantity: ecoTaxItem.quantity,
      unitPrice: ecoTaxItem.unitPrice,
      totalPrice: ecoTaxItem.totalPrice,
      uuid: ecoTaxItem.correlationId || `${parent.uuid}-ecoTax-${ecoTaxIndex}`,
      isEcoTax: true,
      parentIndex,
    }));

    return [parentRow, ...ecoTaxRows];
  });

  const renderIndentedCell = useCallback((params: GridRenderCellParams) => {
    // eslint-disable-next-line prefer-destructuring
    const isEcoTax = params.row.isEcoTax;
    return (
      <span style={{ marginLeft: isEcoTax ? 20 : 0 }}>
        {isEcoTax ? '↳ ' : ''}
        {params.value}
      </span>
    );
  }, []);

  const handleEditRow = (parentIndex: number) => {
    const parentData = rows[parentIndex];
    dispatch(setSelectedItem({ item: parentData, index: parentIndex }));
  };

  const columns: GridColumns = [
    {
      field: 'id',
      headerName: intl.formatMessage({ id: 'receipt.item.input.itemId' }),
      flex: 1,
      sortable: false,
      renderCell: renderIndentedCell,
    },
    {
      field: 'name',
      headerName: intl.formatMessage({ id: 'receipt.item.input.name' }),
      flex: 1.2,
      maxWidth: 350,
      sortable: false,
      renderCell: renderIndentedCell
    },
    {
      field: 'intlUnitOfMeasure',
      headerName: intl.formatMessage({ id: 'receipt.item.input.unitOfMeasure' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'quantity',
      headerName: intl.formatMessage({ id: 'receipt.item.input.quantity' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'unitPrice',
      headerName: intl.formatMessage({ id: 'receipt.item.input.unitPrice' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'totalPrice',
      headerName: intl.formatMessage({ id: 'receipt.item.input.totalPrice' }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: intl.formatMessage({ id: 'receipt.table.actions' }),
      width: 100,
      cellClassName: 'actions',
      getActions: (params) => {
        // Only display actions for parent rows
        if (!params.row.isEcoTax) {
          // eslint-disable-next-line prefer-destructuring
          const parentIndex = params.row.parentIndex;
          return [
            // @ts-ignore mui issue
            <GridActionsCellItem
              key="edit"
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEditRow(parentIndex)}
              className="textPrimary"
              color="inherit"
            />,
            // @ts-ignore mui issue
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => onRemoveRow(parentIndex)}
              color="inherit"
            />,
          ];
        }
        // For eco tax rows no actions 
        return [];
      },
    },
  ];

  return (
    <Box sx={styles.tableContainer}>
      <DataGrid
        disableColumnFilter
        disableColumnMenu
        columns={columns}
        getRowId={(row) => row.uuid}
        rows={flattenedRows}
        rowHeight={35}
        headerHeight={35}
        hideFooter
        autoHeight
        localeText={{
          noRowsLabel: intl.formatMessage({ id: 'receipt.table.empty' }),
        }}
      />
    </Box>
  );
};
